import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { z } from 'zod';
import { LeistungsnachweisPreview } from '../components/LeistungsnachweisPreview';
import { getLogProjectsByEmployee } from '../services/timeEntryService';
import { useSession } from '../contexts/SessionContext';

const searchSchema = z.object({
  project_number: z.string().optional().default(''),
  month: z.string().optional().default(''),
  year: z.string().optional().default(''),
});

export const Route = createFileRoute('/overview/leistungsnachweis' as any)({
  component: LeistungsnachweisComponent,
  validateSearch: searchSchema,
});

function LeistungsnachweisComponent() {
  const search = Route.useSearch() as z.infer<typeof searchSchema>;
  const router = useRouterState();
  const project_number = search.project_number || '';
  const month = search.month || '';
  const year = search.year || '';
  const { employeeId } = useSession();

  const monthNumber = month ? parseInt(month, 10) : undefined;
  const yearNum = year ? parseInt(year, 10) : undefined;
  const projectNumber = project_number || undefined;

  const isValidMonth = monthNumber !== undefined && !isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12;
  const isValidYear = yearNum !== undefined && !isNaN(yearNum) && yearNum > 0;

  const { data: logProjectsData, isLoading, error, refetch } = useQuery({
    queryKey: ['log-projects-by-employee', employeeId, monthNumber, yearNum, projectNumber],
    queryFn: () => {
      if (!employeeId) {
        return Promise.resolve([]);
      }
      return getLogProjectsByEmployee(employeeId, monthNumber, yearNum, projectNumber);
    },
    enabled: !!employeeId && !!month && !!year && isValidMonth && isValidYear,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  useEffect(() => {
    if (employeeId && router.location.pathname === '/overview/leistungsnachweis' && month && year && isValidMonth && isValidYear) {
      refetch();
    }
  }, [employeeId, router.location.pathname, project_number, month, year, isValidMonth, isValidYear, refetch]);

  const { initialProject, initialMonth, initialEmployee } = useMemo(() => {
    if (!logProjectsData || !employeeId || !project_number || !month || !year || !isValidMonth || !isValidYear) {
      return {
        initialProject: '',
        initialMonth: '',
        initialEmployee: '',
      };
    }

    const monthYearKey = `${yearNum}-${String(monthNumber).padStart(2, '0')}`;

    const projectSet = new Set<string>();
    logProjectsData.forEach((entry) => {
      if (entry.project) {
        projectSet.add(entry.project);
      }
    });

    const projects = Array.from(projectSet).sort();
    const selectedProject = projects.length > 0 ? projects[0] : '';

    const employeeSet = new Set<string>();
    logProjectsData.forEach((entry) => {
      if (entry.employee_id && entry.employee_name) {
        employeeSet.add(`${entry.employee_id}|${entry.employee_name}`);
      }
    });

    const employees = Array.from(employeeSet).sort((a, b) => {
      const [, nameA] = a.split('|');
      const [, nameB] = b.split('|');
      return nameA.localeCompare(nameB);
    });

    const selectedEmployee = employees.length > 0 ? employees[0] : '';

    return {
      initialProject: selectedProject,
      initialMonth: monthYearKey,
      initialEmployee: selectedEmployee,
    };
  }, [logProjectsData, employeeId, project_number, month, year, monthNumber, yearNum, isValidMonth, isValidYear]);

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
        Error loading data: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!employeeId || !project_number || !month || !year || !isValidMonth || !isValidYear) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Missing required parameters. Please navigate from the overview page.
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      <LeistungsnachweisPreview
        logProjectsData={logProjectsData || []}
        initialProject={initialProject}
        initialMonth={initialMonth}
        initialEmployee={initialEmployee}
        showDropdowns={false}
      />
    </div>
  );
}
